import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { motion } from "framer-motion";
import { Users, Smartphone, Signal, CreditCard, MessageSquare, TrendingUp, DollarSign, Wallet } from "lucide-react";

export default function ManagerDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin_stats_overview"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "overview" },
      });
      if (error) throw error;
      return data;
    },
  });

  const { data: billing, isLoading: billingLoading } = useQuery({
    queryKey: ["admin_stats_stripe_billing_overview"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "billing" },
      });
      if (error) throw error;
      return data;
    },
  });

  const fmtUsd = (value: number | undefined) => {
    if (value === undefined || value === null) return "—";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
  };

  const fmtBrl = (value: number | undefined) => {
    if (value === undefined || value === null) return null;
    const rate = billing?.usdToBrl;
    if (!rate) return null;
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value * rate);
  };

  const cards = [
    { label: "Total de Contas", value: data?.totalAccounts, icon: Users, accent: "text-primary" },
    { label: "Instâncias Online", value: `${data?.onlineInstances || 0}/${data?.totalInstances || 0}`, icon: Signal, accent: "text-accent" },
    { label: "Assinaturas Ativas", value: data?.activeSubs, icon: CreditCard, accent: "text-green-400" },
    { label: "Msgs Enviadas Hoje", value: data?.todaySent?.toLocaleString("pt-BR"), icon: MessageSquare, accent: "text-orange-400" },
    { label: "Msgs Recebidas Hoje", value: data?.todayReceived?.toLocaleString("pt-BR"), icon: TrendingUp, accent: "text-blue-400" },
    { label: "Total Instâncias", value: data?.totalInstances, icon: Smartphone, accent: "text-purple-400" },
  ];

  const billingCards = [
    { label: "MRR", value: billing?.mrr, icon: TrendingUp, accent: "text-accent" },
    { label: "Receita (30 dias)", value: billing?.totalRevenue30d, icon: DollarSign, accent: "text-primary" },
    { label: "Saldo Disponível", value: billing?.availableBalance, icon: Wallet, accent: "text-green-400" },
    { label: "Cobranças (30 dias)", value: billing?.chargesCount30d, icon: CreditCard, accent: "text-orange-400", isCount: true },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">Visão Geral</h1>
        <div className="flex items-center gap-3 mt-1">
          <p className="text-muted-foreground text-sm">
            Métricas gerais da plataforma WarmZapp
          </p>
          {billing?.usdToBrl && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary/60 text-muted-foreground">
              $1 = R$ {billing.usdToBrl.toFixed(2)}
            </span>
          )}
        </div>
      </div>

      {/* Platform Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
          >
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="w-11 h-11 rounded-xl bg-secondary/60 flex items-center justify-center">
                  <c.icon className={`h-5 w-5 ${c.accent}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{c.label}</p>
                  <p className="font-display text-xl font-bold text-foreground">
                    {isLoading ? <Skeleton className="h-6 w-16" /> : (c.value ?? "—")}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Billing Summary */}
      <div>
        <h2 className="font-display text-lg font-semibold text-foreground mb-3">💰 Faturamento</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {billingCards.map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.06 }}
            >
              <Card className="glass-card border-warmz-glass-border/50">
                <CardContent className="flex items-center gap-4 p-5">
                  <div className="w-11 h-11 rounded-xl bg-secondary/60 flex items-center justify-center">
                    <c.icon className={`h-5 w-5 ${c.accent}`} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{c.label}</p>
                    <p className="font-display text-xl font-bold text-foreground">
                      {billingLoading ? <Skeleton className="h-6 w-20" /> : (
                        c.isCount ? (c.value ?? "—") : fmtUsd(c.value)
                      )}
                    </p>
                    {!c.isCount && fmtBrl(c.value) && (
                      <p className="text-[10px] text-muted-foreground/70 mt-0.5">≈ {fmtBrl(c.value)}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Profile Distribution */}
      {data?.profileDistribution && data.profileDistribution.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="p-5 space-y-3">
              <h2 className="font-display text-lg font-semibold text-foreground">Distribuição por Perfil de Aquecimento</h2>
              <div className="space-y-2">
                {data.profileDistribution.map((p: any) => (
                  <div key={p.name} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{p.icon}</span>
                      <span className="text-sm font-medium text-foreground">{p.name}</span>
                    </div>
                    <span className="text-sm font-bold text-primary">{p.count} instâncias</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
